import { Component, OnInit } from '@angular/core';
import { IAgent } from '../../../../models/agents.models';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import * as agentsActions  from '../../../../store/agents.actions';
import { map, Observable, startWith, takeUntil, combineLatest } from 'rxjs';
import { Subject } from 'rxjs';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { FormControl } from '@angular/forms';
import { FetchBrands } from 'src/app/features/brands/store/brands.actions';
import { MatChipInputEvent } from '@angular/material/chips';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { FetchTags } from 'src/app/features/tags/store/tags.actions';
import { FetchCategories } from 'src/app/features/categories/store/categories.actions';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';

@Component({
  selector: 'app-agent-product-preferences-dialog',
  templateUrl: './agent-product-preferences-dialog.component.html',
  styleUrls: ['./agent-product-preferences-dialog.component.scss']
})
export class AgentProductPreferencesDialogComponent implements OnInit {
  agent: IAgent | null = null;
  type: 'blacklist' | 'whitelist' = 'whitelist';
  brands$: Observable<IBrand[]> = this.store.select((state: AppState) => state.brands.brands);
  labels$: Observable<ITag[]> = this.store.select((state: AppState) => state.tags.tags);
  categories$: Observable<ICategory[]> = this.store.select((state: AppState) => state.categories.categories);

  brands: IBrand[] = [];
  labels: ITag[] = [];
  categories: ICategory[] = [];

  filteredBrands!: Observable<IBrand[]>;
  filteredLabels!: Observable<ITag[]>;
  filteredCategories!: Observable<ICategory[]>;

  brandControl = new FormControl('');
  labelControl = new FormControl('');
  categoryControl = new FormControl('');

  selectedBrands: IBrand[] = [];
  selectedLabels: ITag[] = [];
  selectedCategories: ICategory[] = [];

  destroy$ = new Subject<void>();
  separatorKeysCodes: number[] = [ENTER, COMMA];

  excludedBrandIds: number[] = [];
  excludedLabelIds: number[] = [];
  excludedCategoryIds: number[] = [];

  constructor(
    private store: Store<AppState>,
    private dialogRef: MatDialogRef<AgentProductPreferencesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    this.agent = this.data.agent;
    this.type = this.data.type;

    // Fetch all brands, labels and categories
    this.store.dispatch(FetchBrands({page: {number: 0, size: 0}}));
    this.store.dispatch(FetchTags({page: {number: 0, size: 0}}));
    this.store.dispatch(FetchCategories({page: {number: 0, size: 0}}));

    // Set excluded IDs based on type
    // If the agent has a whitelist, we need to exclude the brands, labels and categories that are in the blacklist
    // If the agent has a blacklist, we need to exclude the brands, labels and categories that are in the whitelist
    if (this.agent) {
      if (this.type === 'whitelist') {
        this.excludedBrandIds = (this.agent.black_list_brands as IBrand[] || []).map(b => b.id);
        this.excludedLabelIds = (this.agent.black_list_tags as ITag[] || []).map(l => l.id);
        this.excludedCategoryIds = (this.agent.black_list_categories as ICategory[] || []).map(c => c.id);
      } else if (this.type === 'blacklist') {
        this.excludedBrandIds = (this.agent.white_list_brands as IBrand[] || []).map(b => b.id);
        this.excludedLabelIds = (this.agent.white_list_tags as ITag[] || []).map(l => l.id);
        this.excludedCategoryIds = (this.agent.white_list_categories as ICategory[] || []).map(c => c.id);
      }
    }

    // Filter brands, labels and categories based on the input value
    this.filteredBrands = combineLatest([
      this.brandControl.valueChanges.pipe(startWith('')),
      this.brands$.pipe(takeUntil(this.destroy$))
    ]).pipe(
      map(([value, brands]) => {
        this.brands = brands.filter(brand => brand.name && brand.name.trim() !== '' && !this.excludedBrandIds.includes(brand.id));
        return this.filterSelectableItems(value || '', this.brands, this.selectedBrands);
      })
    );

    this.filteredLabels = combineLatest([
      this.labelControl.valueChanges.pipe(startWith('')),
      this.labels$.pipe(takeUntil(this.destroy$))
    ]).pipe(
      map(([value, labels]) => {
        this.labels = labels.filter(label => label.name && label.name.trim() !== '' && !this.excludedLabelIds.includes(label.id));
        return this.filterSelectableItems(value || '', this.labels, this.selectedLabels);
      })
    );

    this.filteredCategories = combineLatest([
      this.categoryControl.valueChanges.pipe(startWith('')),
      this.categories$.pipe(takeUntil(this.destroy$))
    ]).pipe(
      map(([value, categories]) => {
        this.categories = categories.filter(category => category.name && category.name.trim() !== '' && !this.excludedCategoryIds.includes(category.id));
        return this.filterSelectableItems(value || '', this.categories, this.selectedCategories);
      })
    );

    // Set the selected brands, labels and categories based on the type
    if (this.type === 'blacklist') {
      this.selectedBrands = [...(this.agent?.black_list_brands as IBrand[] || [])];
      this.selectedLabels = [...(this.agent?.black_list_tags as ITag[] || [])];
      this.selectedCategories = [...(this.agent?.black_list_categories as ICategory[] || [])];
    } else {
      this.selectedBrands = [...(this.agent?.white_list_brands as IBrand[] || [])];
      this.selectedLabels = [...(this.agent?.white_list_tags as ITag[] || [])];
      this.selectedCategories = [...(this.agent?.white_list_categories as ICategory[] || [])];
    }

    // Close the dialog when the agent is updated
    this.actions$.pipe(
      ofType(agentsActions.UpdateAgentSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.dialogRef.close();
    });
  }

  // Select a brand
  selectBrand(brand: IBrand) {
    if (!this.selectedBrands.some(b => b.id === brand.id)) {
      this.selectedBrands.push(brand);
    }
    this.brandControl.setValue('');
  }

  // Remove a brand
  removeBrand(brand: IBrand) {
    this.selectedBrands = this.selectedBrands.filter(b => b.id !== brand.id);
    // Trigger input change to refresh filtered list
    this.brandControl.setValue(this.brandControl.value);
  }

  // Add a brand from the input
  addBrandFromInput(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value?.trim();

    if (value) {
      const match = this.brands.find(b => b.name.toLowerCase() === value.toLowerCase());
      if (match && !this.selectedBrands.some(b => b.id === match.id)) {
        this.selectedBrands.push(match);
      }
    }
    if (input) {
      input.value = '';
    }
    this.brandControl.setValue('');
  }

  // Select a label
  selectLabel(label: any) {
    if (!this.selectedLabels.some(l => l.id === label.id)) {
      this.selectedLabels.push(label);
    }
    this.labelControl.setValue('');
  }

  // Remove a label
  removeLabel(label: any) {
    this.selectedLabels = this.selectedLabels.filter(l => l.id !== label.id);
    // Trigger input change to refresh filtered list
    this.labelControl.setValue(this.labelControl.value);
  }

  // Add a label from the input
  addLabelFromInput(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value?.trim();
    if (value) {
      const match = this.labels.find(l => l.name.toLowerCase() === value.toLowerCase());
      if (match && !this.selectedLabels.some(l => l.id === match.id)) {
        this.selectedLabels.push(match);
      }
    }
    if (input) {
      input.value = '';
    }
    this.labelControl.setValue('');
  }

  // Select a category
  selectCategory(category: any) {
    if (!this.selectedCategories.some(c => c.id === category.id)) {
      this.selectedCategories.push(category);
    }
    this.categoryControl.setValue('');
  }

  // Remove a category
  removeCategory(category: any) {
    this.selectedCategories = this.selectedCategories.filter(c => c.id !== category.id);
    // Trigger input change to refresh filtered list
    this.categoryControl.setValue(this.categoryControl.value);
  }

  // Add a category from the input
  addCategoryFromInput(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value?.trim();
    if (value) {
      const match = this.categories.find(c => c.name.toLowerCase() === value.toLowerCase());
      if (match && !this.selectedCategories.some(c => c.id === match.id)) {
        this.selectedCategories.push(match);
      }
    }
    if (input) {
      input.value = '';
    }
    this.categoryControl.setValue('');
  }

  /**
   * Filters items for use in chips+autocomplete multiselects.
   * Removes items with empty name, removes already selected, and filters by input value if string.
   */
  private filterSelectableItems<T extends { id: number; name: string }>(
    value: string | T,
    items: T[],
    selected: T[]
  ): T[] {
    // Remove items with empty name
    const validItems = items.filter(item => item.name && item.name.trim() !== '');
    // Remove already selected items
    let availableItems = validItems.filter(
      item => !selected.some(sel => sel.id === item.id)
    );
    // Only filter by name if value is a string
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return availableItems.filter(item => item.name.toLowerCase().includes(filterValue));
    }
    // If value is an object (after selection), show all available items
    return availableItems;
  }

  // Save the selected brands, labels and categories
  save() {
    if (!this.agent?.id) return;

    const patch: Partial<IAgent> = {};
    if (this.type === 'blacklist') {
      patch.black_list_brands = this.selectedBrands.map(b => b.id);
      patch.black_list_tags = this.selectedLabels.map(l => l.id);
      patch.black_list_categories = this.selectedCategories.map(c => c.id);
    } else {
      patch.white_list_brands = this.selectedBrands.map(b => b.id);
      patch.white_list_tags = this.selectedLabels.map(l => l.id);
      patch.white_list_categories = this.selectedCategories.map(c => c.id);
    }

    this.store.dispatch(agentsActions.UpdateAgent({
      id: this.agent.id!,
      agent: patch
    }));
  }

  cancel() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
